import PERMISSIONS_CONSTANTS from '@/constants/permission-constants';
import { useOrganizationStore } from '@/stores/organization.store';
import { useProjectsStore } from '@/stores/projects.store';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';
import DashboardHamburgerMenuComponent from '../dashboard-layout/dashboard-hamburger-menu.component';
import MetaInfo from '../meta-info/meta-info.component';
import { CLPrimaryButton } from '../ui-controls';
import CreateProjectPopupComponent from './create-project-popup.component';

export default function ProjectsComponent() {
  // Variables
  const router = useRouter();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Store
  const [selectedOrganization, organizationPermissions] = useOrganizationStore(
    s => [s.selectedOrganization, s.organizationPermissions],
    shallow
  );
  const [isLoading, projects] = useProjectsStore(
    s => [s.isLoading, s.projects],
    shallow
  );

  if (isLoading || projects?.length > 0) {
    // Redirect to first project
    if (projects?.length > 0) {
      router.push('/projects/' + projects[0]?.id);
    }

    return (
      <div className="px-4 md:px-10 pt-6 pb-6">
        <div className="bg-[#EDE4E0] h-7 rounded-lg w-52 max-w-full mb-6"></div>

        <ul className="grid grid-cols-1 gap-3 mt-8">
          <li className="bg-[#EDE4E0] rounded-lg h-12"></li>
          <li className="bg-[#EDE4E0] rounded-lg h-12"></li>
          <li className="bg-[#EDE4E0] rounded-lg h-12"></li>
          <li className="bg-[#EDE4E0] rounded-lg h-12"></li>
        </ul>
      </div>
    );
  }

  // Meta information
  const metaInfo = {
    description:
      'checklist.gg is an AI-driven checklists, processes and SOPs management tool designed to help organizations streamline and optimize their workflow. It allows users to create, assign, and track tasks and checklists in real-time, ensuring that all team members are on the same page and that tasks are completed efficiently and effectively.',
    keywords:
      'checklist software, SOP software, checklist management, SOP management, process management, AI checklist, Checklist manifesto',
    metaTitle: 'An AI-driven tool for checklists, processes, and SOPs',
    url: 'https://app.checklist.gg/projects/',
    image:
      `https://app.checklist.gg/api/og?title=` +
      'An AI-driven tool for checklists, processes, and SOPs'
  };

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  return (
    <>
      <Head>
        <title>Projects</title>
        <MetaInfo metaInfo={metaInfo}></MetaInfo>
      </Head>

      <div className="px-4 md:px-10">
        {/* Toolbar */}
        <div className="block md:hidden flex flex-wrap items-center justify-between gap-4 sticky top-0 py-4 md:py-6 cl_primary_background z-[1]">
          <div className="flex gap-1 justify-center items-center">
            {/* Hamburger menu  */}
            <div className="block md:hidden">
              <DashboardHamburgerMenuComponent />
            </div>

            {/* Title */}
            <div className="text-xl font-bold">Projects</div>
          </div>
        </div>

        {/* Body */}
        {/* If permission is there */}
        {organizationPermissions?.[PERMISSIONS_CONSTANTS.CREATE_PROJECT] ? (
          <div className="flex flex-col items-center gap-8 mt-[12%]">
            <div className="text-center text-3xl font-normal max-w-3xl mx-auto">
              Save time by letting AI generate teams/projects for your industry
              in seconds, rather than manually creating them.
            </div>

            {/* {organizationPermissions?.[PERMISSIONS_CONSTANTS.CREATE_PROJECT] && ( */}
            <div className="flex flex-wrap items-center justify-center">
              <CLPrimaryButton onClick={openModal}>
                <svg
                  className="w-3 mr-2"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 0C7.55229 0 8 0.447715 8 1V6H13C13.5523 6 14 6.44772 14 7C14 7.55229 13.5523 8 13 8H8V13C8 13.5523 7.55229 14 7 14C6.44772 14 6 13.5523 6 13V8H1C0.447715 8 0 7.55229 0 7C0 6.44772 0.447715 6 1 6H6V1C6 0.447715 6.44772 0 7 0Z"
                    fill="black"
                  />
                </svg>
                New Project
              </CLPrimaryButton>
            </div>
            {/* )} */}
          </div>
        ) : (
          // No permission to create project
          <div className="flex flex-col items-center gap-8 mt-[12%]">
            <div className="text-center text-3xl font-normal max-w-3xl mx-auto">
              You currently don&apos;t have access to projects in{' '}
              {selectedOrganization?.name}. Please request the owner to grant
              access.
            </div>
          </div>
        )}
      </div>

      <CreateProjectPopupComponent isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );
}
